/** Witness statement wizard — shared bits (Dean, 14 Oct 2026). */
export const STEPS = [
  { key: "setup", label: "Set up" },
  { key: "exhibits", label: "Exhibits" },
  { key: "draft", label: "Draft" },
  { key: "review", label: "Review" },
  { key: "finish", label: "Finish" },
];

/** Which stage a map step usually calls for (the author can change it). */
export const STAGE_FOR_STEP = {
  bk_petition: "support_petition", bk_hearing: "support_petition", wu_hearing: "support_petition",
  sdd_received: "oppose_set_aside", sdd_hearing: "oppose_set_aside",
  def_filed: "respond_defence", def_directions: "respond_defence", def_trial: "respond_defence", sa_received: "respond_defence", sa_listed: "respond_defence",
  ccj_issued: "support_claim",
};

export const STAGE_TONE = {
  support_claim: "border-sky-300 bg-sky-50 text-sky-900",
  respond_defence: "border-amber-300 bg-amber-50 text-amber-900",
  support_petition: "border-rose-300 bg-rose-50 text-rose-900",
  oppose_set_aside: "border-violet-300 bg-violet-50 text-violet-900",
};

export const KIND_LABEL = { intro: "Intro", fact: "Fact", law: "Law", closing: "Closing", heading: "Heading" };

export const initialsOf = (name) => ((name || "").trim().split(/\s+/).filter(Boolean).slice(0, 3).map((p) => p[0].toUpperCase()).join("") || "WS");

/** Re-number the exhibits PD 32 style after a pick / reorder: initials + 1, 2, 3… — and rewrite "(Exhibit JA2)" in the text to follow. */
export const relabel = (exhibits, deponentName) => {
  const ini = initialsOf(deponentName);
  return exhibits.map((e, i) => ({ ...e, label: `${ini}${i + 1}` }));
};

export const relabelAll = (exhibits, deponentName, paragraphs) => {
  const next = relabel(exhibits, deponentName);
  const mapping = Object.fromEntries(exhibits.map((e, i) => [e.label, next[i].label]).filter(([a, b]) => a && a !== b));
  if (!Object.keys(mapping).length || !paragraphs) return { exhibits: next, paragraphs };
  const rx = new RegExp(`\\bExhibit\\s+(${Object.keys(mapping).map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})\\b`, "g");
  return { exhibits: next, paragraphs: paragraphs.map((p) => (rx.test(p.html || "") ? { ...p, html: (p.html || "").replace(rx, (_, l) => `Exhibit ${mapping[l]}`) } : p)) };
};

/** [date served]-style tokens still in the text — highlighted yellow in the editor and the draft PDF, must be replaced before filing. */
export const PLACEHOLDER_RX = /\[[^[\]\n]{1,80}\]/g;
export const placeholdersOf = (paragraphs) => {
  const out = [];
  (paragraphs || []).forEach((p) => { for (const m of (p.html || "").replace(/<[^>]+>/g, " ").matchAll(PLACEHOLDER_RX)) if (!out.includes(m[0])) out.push(m[0]); });
  return out.map((t) => t.replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">"));
};

export const errMsg = (e) => { const d = e?.response?.data?.detail; return (typeof d === "string" ? d : d?.message) || e.message; };

export const fmtSize = (n) => (n == null ? "" : n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

/** Append "(Exhibit JA1)" to the end of a paragraph's html (before the closing tag). */
export const appendExhibit = (html, label) => {
  const tag = ` (Exhibit ${label})`;
  const h = (html || "").trim();
  if (!h) return `<p>${tag.trim()}</p>`;
  const m = h.match(/<\/(p|li|div)>\s*$/);
  return m ? h.slice(0, m.index) + tag + h.slice(m.index) : `${h}<p>${tag.trim()}</p>`;
};

export const newParagraph = (kind = "fact", html = "") => ({ id: `p${Math.random().toString(36).slice(2, 10)}`, block_id: null, kind, title: null, html, text: "", date: null, exhibits: [], basis: "records" });

export const blobUrl = (blob, type) => URL.createObjectURL(type ? new Blob([blob], { type }) : blob);
export const download = (blob, name) => { const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 5000); };

/** Placeholders left in the heading box (court · case number · title lines · parties) — shown, not blocking: a claim number may not exist yet. */
export const headingPlaceholders = (heading) => {
  const h = heading || {};
  const texts = [h.court, h.case_number, ...(h.lines || []), ...(h.parties || []).flatMap((p) => [p.name, p.role])].filter(Boolean);
  const out = [];
  texts.forEach((t) => { for (const m of String(t).matchAll(PLACEHOLDER_RX)) if (!out.includes(m[0])) out.push(m[0]); });
  return out;
};
